import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Image, Text } from '@chakra-ui/react';
import { ChevronRight, ArrowForward, LocalShippingOutlined } from '@mui/icons-material';

import FloatingDrawer from './FloatingDrawer';
import CartLine from '../CartLine';
import { useCartContext } from '../../contexts/CartContext';
import { useShopUI } from '../../contexts/ShopUIContext';
import useCatalog, { categoryImage } from '../../hooks/useCatalog';
import { formatPrice } from '../product/productUtils';

const condensed = { fontWeight: 700, textTransform: 'uppercase', sx: { fontStretch: '75%' } };

// Cart drawer (design-system.md §4.6): items, free-delivery note, order summary, PROCEED TO CHECKOUT;
// desktop adds a "You may also like" column. Empty state links to categories.
const CartDrawer = () => {

    const navigate = useNavigate();
    const { cartOpen, closeCart } = useShopUI();
    const { items, itemCount, subtotal, setItemAmount, removeItem } = useCartContext();
    const { categories, products } = useCatalog();
    const byId = useMemo(() => Object.fromEntries(products.map(p => [p._id, p])), [products]);
    const suggestions = useMemo(() => {
        const inCart = new Set(items.map(i => i.id));
        return [...products].reverse().filter(p => !inCart.has(p._id)).slice(0, 3);
    }, [products, items]);

    const go = (path, state) => { closeCart(); navigate(path, state ? { state } : undefined); };
    const empty = items.length === 0;

    const title = (
        <Text fontSize='16px' {...condensed} >Your cart <Box as='span' fontWeight={400} sx={{ fontStretch: '100%' }} textTransform='none' fontSize='14px' >({itemCount} {itemCount === 1 ? 'item' : 'items'})</Box></Text>
    );

    const footer = !empty && (
        <Box display='flex' alignItems='center' gap='16px' >
            <Box>
                <Text textStyle='condensed' fontSize='20px' >{formatPrice(subtotal, { space: false })}</Text>
                <Text fontSize='12px' color='raano.muted' >Subtotal</Text>
            </Box>
            <Button variant='ink' flex={1} height='48px' fontSize='16px' onClick={() => go('/cart')} >Proceed to checkout</Button>
        </Box>
    );

    return (
        <FloatingDrawer isOpen={cartOpen} onClose={closeCart} title={title} width={empty ? '500px' : '640px'} footer={footer} label='Cart' >
            {empty ? (
                <Box px={{ base: '16px', md: '28px' }} py='48px' textAlign='center' >
                    <Text fontSize={{ base: '26px', md: '30px' }} {...condensed} >Your cart is empty</Text>
                    <Text mt='8px' fontSize='14px' color='raano.muted' >Not sure where to start? Try one of these.</Text>
                    <Box mt='24px' display='flex' flexDirection='column' gap='10px' maxWidth='340px' mx='auto' >
                        {categories.slice(0, 3).map(c => (
                            <Box key={c._id} as='button' display='flex' alignItems='center' gap='14px' p='10px' border='1px solid' borderColor='raano.border' borderRadius='8px' textAlign='left' _hover={{ borderColor: 'raano.black' }} onClick={() => go('/search', { categoryId: c._id })} >
                                <Box width='56px' height='56px' bg='raano.imageBg' flexShrink={0} >{categoryImage(products, c._id) && <Image src={categoryImage(products, c._id)} alt='' width='100%' height='100%' objectFit='cover' />}</Box>
                                <Text flex={1} fontSize='15px' fontWeight={600} >{c.name}</Text>
                                <ChevronRight />
                            </Box>
                        ))}
                    </Box>
                    <Button mt='24px' variant='ink' height='48px' px='40px' fontSize='16px' rightIcon={<ArrowForward fontSize='small' />} onClick={() => go('/search', {})} >Shop all</Button>
                </Box>
            ) : (
                <Box display='flex' height='100%' >
                    {/* You may also like (desktop) */}
                    <Box display={{ base: 'none', md: 'block' }} width='170px' flexShrink={0} borderRight='1px solid' borderColor='raano.border' p='14px' overflowY='auto' >
                        <Text fontSize='13px' {...condensed} mb='10px' >You may also like</Text>
                        {suggestions.map(p => (
                            <Box key={p._id} as='button' display='block' width='100%' textAlign='left' mb='14px' onClick={() => go(`/product/${p._id}`, { productId: p._id })} >
                                <Box bg='raano.imageBg' sx={{ aspectRatio: '2 / 3' }} ><Image src={p.imageUrl} alt={p.name} width='100%' height='100%' objectFit='cover' /></Box>
                                <Text fontSize='12px' mt='6px' noOfLines={1} >{p.name}</Text>
                                <Text textStyle='condensed' fontSize='13px' >{formatPrice(p.price)}</Text>
                            </Box>
                        ))}
                    </Box>

                    <Box flex={1} minWidth={0} px={{ base: '16px', md: '18px' }} overflowY='auto' >
                        <Box display='flex' alignItems='center' gap='8px' mt='14px' px='12px' py='10px' bg='raano.surface' borderRadius='8px' fontSize='13px' >
                            <LocalShippingOutlined sx={{ fontSize: 18 }} />
                            <Text>Congratulations! Your order is eligible for <b>Free Delivery</b>.</Text>
                        </Box>
                        <Box height='4px' bg='raano.black' borderRadius='full' mt='8px' />

                        {items.map(item => (
                            <CartLine key={item.id} compact item={item} product={byId[item.id]} onChangeAmount={(n) => setItemAmount(item.id, n)} onRemove={() => removeItem(item.id)} />
                        ))}

                        <Text mt='18px' fontSize='15px' fontWeight={600} >Order summary</Text>
                        <Box fontSize='14px' mt='8px' mb='16px' >
                            <Box display='flex' justifyContent='space-between' py='4px' ><Text color='raano.muted' >Order value</Text><Text>{formatPrice(subtotal, { space: false })}</Text></Box>
                            <Box display='flex' justifyContent='space-between' py='4px' ><Text color='raano.muted' >Shipping fee</Text><Text color='raano.success' fontWeight={600} >FREE</Text></Box>
                            <Box display='flex' justifyContent='space-between' pt='10px' mt='6px' borderTop='1px solid' borderColor='raano.border' ><Text fontWeight={700} >GRAND TOTAL</Text><Text fontWeight={700} >{formatPrice(subtotal, { space: false })}</Text></Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </FloatingDrawer>
    );
};

export default CartDrawer;
